const PRESENTATION_EXTENSIONS = new Set([
  "ppt",
  "pptx",
  "ppx",
  "pptm",
  "pps",
  "ppsx",
  "pot",
  "potx",
  "potm",
]);

const ID_FIELDS = [
  "id",
  "fileId",
  "documentId",
  "attachmentId",
  "blobId",
  "storageId",
];

const URL_FIELDS = ["url", "fileUrl", "link", "downloadUrl", "previewUrl"];

const TOP_LEVEL_PREVIEW_ID_FIELDS = [
  "previewFileId",
  "previewId",
  "previewDocumentId",
  "previewAttachmentId",
  "pdfFileId",
  "pdfPreviewFileId",
  "pdfPreviewId",
  "convertedFileId",
  "convertedPreviewFileId",
  "renditionFileId",
  "pdfRenditionFileId",
];

const TOP_LEVEL_PREVIEW_URL_FIELDS = [
  "previewUrl",
  "pdfPreviewUrl",
  "previewFileUrl",
  "renditionUrl",
  "pdfRenditionUrl",
  "convertedFileUrl",
];

const TOP_LEVEL_PREVIEW_NAME_FIELDS = [
  "previewFileName",
  "previewName",
  "pdfFileName",
  "pdfPreviewFileName",
  "renditionFileName",
  "convertedFileName",
];

const TOP_LEVEL_PREVIEW_TYPE_FIELDS = [
  "previewContentType",
  "previewMimeType",
  "pdfContentType",
  "pdfPreviewContentType",
  "renditionContentType",
];

const asObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : null;

const firstPresent = (...values) =>
  values.find((value) => value != null && String(value).trim().length > 0);

const pickField = (source, fields) => {
  if (!source) return undefined;
  return firstPresent(...fields.map((field) => source[field]));
};

export const getFileExtension = (value) => {
  const cleanValue = String(value || "").split("?")[0].split("#")[0];
  const segment = cleanValue.split("/").pop() || "";
  const parts = segment.split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
};

export const getPdfPreviewName = (file) => {
  const raw = file?.raw || file || {};
  const name =
    file?.name || raw?.name || raw?.fileName || raw?.documentName || raw?.title || "";

  if (!name) return "preview.pdf";
  if (/\.[a-z0-9]{2,8}$/i.test(name)) {
    return name.replace(/\.[a-z0-9]{2,8}$/i, ".pdf");
  }
  return `${name}.pdf`;
};

export const isPresentationFile = (file) => {
  const raw = file?.raw || file || {};
  const name =
    file?.name || raw?.name || raw?.fileName || raw?.documentName || raw?.title || "";
  const contentType = String(
    file?.contentType || raw?.contentType || raw?.mimeType || "",
  ).toLowerCase();
  const extension = getFileExtension(name);

  return (
    PRESENTATION_EXTENSIONS.has(extension) ||
    contentType.includes("presentation") ||
    contentType.includes("powerpoint")
  );
};

const isPdfCandidate = (candidate) => {
  const contentType = String(
    candidate?.contentType || candidate?.mimeType || "",
  ).toLowerCase();
  const format = String(candidate?.format || candidate?.type || "").toLowerCase();
  const name =
    candidate?.name ||
    candidate?.fileName ||
    candidate?.documentName ||
    candidate?.title ||
    "";
  const url = pickField(candidate, URL_FIELDS) || "";

  return (
    contentType.includes("pdf") ||
    format === "pdf" ||
    getFileExtension(name) === "pdf" ||
    getFileExtension(url) === "pdf"
  );
};

const buildCandidateTarget = (candidate, fallbackName, restricted) => {
  if (!asObject(candidate) || !isPdfCandidate(candidate)) return null;

  const fileId = pickField(candidate, ID_FIELDS);
  const url = pickField(candidate, URL_FIELDS);
  if (!fileId && !url) return null;

  return {
    fileId,
    url: url || "",
    name:
      candidate.name ||
      candidate.fileName ||
      candidate.documentName ||
      candidate.title ||
      fallbackName,
    contentType: candidate.contentType || candidate.mimeType || "application/pdf",
    restricted,
    raw: candidate,
  };
};

const collectPreviewCandidates = (raw) => {
  const candidates = [];

  [
    raw?.preview,
    raw?.previewFile,
    raw?.previewRendition,
    raw?.pdfPreview,
    raw?.convertedPreview,
    raw?.convertedFile,
    raw?.rendition,
    raw?.previews?.pdf,
    raw?.previewFiles?.pdf,
    raw?.previewRenditions?.pdf,
    raw?.renditions?.pdf,
  ].forEach((candidate) => {
    const objectCandidate = asObject(candidate);
    if (objectCandidate) candidates.push(objectCandidate);
  });

  ["previews", "previewFiles", "previewRenditions", "renditions"].forEach(
    (field) => {
      const value = raw?.[field];
      if (Array.isArray(value)) {
        value.forEach((candidate) => {
          const objectCandidate = asObject(candidate);
          if (objectCandidate) candidates.push(objectCandidate);
        });
      }
    },
  );

  return candidates;
};

const getPreviewRenditionTarget = (file) => {
  const raw = file?.raw || file || {};
  const restricted = Boolean(file?.restricted || raw?.restricted);
  const fallbackName = getPdfPreviewName(file);

  const explicitFileId = pickField(raw, TOP_LEVEL_PREVIEW_ID_FIELDS);
  const explicitUrl = pickField(raw, TOP_LEVEL_PREVIEW_URL_FIELDS);

  if (explicitFileId || explicitUrl) {
    return {
      fileId: explicitFileId,
      url: explicitUrl || "",
      name: pickField(raw, TOP_LEVEL_PREVIEW_NAME_FIELDS) || fallbackName,
      contentType: pickField(raw, TOP_LEVEL_PREVIEW_TYPE_FIELDS) || "application/pdf",
      restricted,
      raw,
    };
  }

  return (
    collectPreviewCandidates(raw)
      .map((candidate) =>
        buildCandidateTarget(candidate, fallbackName, restricted),
      )
      .find(Boolean) || null
  );
};

export const resolvePreviewTarget = (file) => {
  if (!isPresentationFile(file)) {
    return {
      method: "download",
      target: file,
      usesGeneratedPreview: false,
    };
  }

  const renditionTarget = getPreviewRenditionTarget(file);
  if (renditionTarget) {
    return {
      method: renditionTarget.fileId ? "download" : "url",
      target: renditionTarget,
      usesGeneratedPreview: true,
    };
  }

  return {
    method: "download",
    target: file,
    usesGeneratedPreview: false,
  };
};

export const getPreviewUnavailableMessage = (file) => {
  if (isPresentationFile(file)) {
    return "PowerPoint preview could not be opened. You can download the file and open it in PowerPoint.";
  }

  return "Could not preview this file.";
};
